import { QUESTION_TYPES } from '@api/instant_survey';
import { VIEWABLE_ADDONS } from '@api/instant_survey/utilities/constants';

import {
  BookOpenIcon,
  CalculatorIcon,
  ChartBarIcon,
  CheckSquareIcon,
  GridIcon,
  InstructionalTextIcon,
  MaxDiffIcon,
  NumericIcon,
  RadioButtonIcon,
  RankedIcon,
  TextTIcon,
  VideoIcon,
} from '@utilities/icons';

/**
 * gets question icon based on question's type
 * @param  {String} type
 * @return {React.ReactNode}
 */
export const getQuestionTypeIcon = (type) => {
  switch (type) {
    case QUESTION_TYPES.CHECKBOX.value:
      return <CheckSquareIcon />;
    case QUESTION_TYPES.CONT_SUM.value:
    case QUESTION_TYPES.CONTSUM.value:
      return <CalculatorIcon />;
    case QUESTION_TYPES.ESSAY.value:
      return <BookOpenIcon />;
    case QUESTION_TYPES.INSTRUCTIONS.value:
      return <InstructionalTextIcon />;
    case QUESTION_TYPES.MAX_DIFF.externalType:
    case QUESTION_TYPES.MAX_DIFF.value:
      return <MaxDiffIcon />;
    case QUESTION_TYPES.NUMERIC.externalType:
    case QUESTION_TYPES.NUMERIC.value:
      return <NumericIcon />;
    case QUESTION_TYPES.RADIO.value:
      return <RadioButtonIcon />;
    case QUESTION_TYPES.RANK.value:
    case QUESTION_TYPES['RANK-DRAGDROP'].value:
      return <RankedIcon />;
    case QUESTION_TYPES.SEQUENTIAL_MONADIC.value:
    case QUESTION_TYPES.TABLE.value:
    case QUESTION_TYPES['TABLE-RADIO'].value:
      return <GridIcon />;
    case QUESTION_TYPES.TURF.value:
      return <ChartBarIcon />;
    case QUESTION_TYPES.VAN_WESTENDORP.value:
      return <ChartBarIcon />; // TODO: swap for dedicated VW icon when provided
    case QUESTION_TYPES.VIDEO.value:
      return <VideoIcon />;
    default:
      return <TextTIcon />;
  }
};

/**
 * gets question type name based on key (reverse lookup for value or externalKey)
 * @param  {String} type
 * @return {String}
 */
export const getQuestionTypeName = (type) => {
  switch (type) {
    case QUESTION_TYPES.CHECKBOX.value:
      return QUESTION_TYPES.CHECKBOX.name;
    case QUESTION_TYPES.CONT_SUM.value:
    case QUESTION_TYPES.CONTSUM.value:
      return QUESTION_TYPES.CONT_SUM.name;
    case QUESTION_TYPES.ESSAY.value:
      return QUESTION_TYPES.ESSAY.name;
    case QUESTION_TYPES.INSTRUCTIONS.value:
      return QUESTION_TYPES.INSTRUCTIONS.name;
    case QUESTION_TYPES.MAX_DIFF.externalType:
    case QUESTION_TYPES.MAX_DIFF.value:
      return QUESTION_TYPES.MAX_DIFF.name;
    case QUESTION_TYPES.NUMERIC.externalType:
    case QUESTION_TYPES.NUMERIC.value:
      return QUESTION_TYPES.NUMERIC.name;
    case QUESTION_TYPES.RADIO.value:
      return QUESTION_TYPES.RADIO.name;
    case QUESTION_TYPES.RANK.value:
    case QUESTION_TYPES['RANK-DRAGDROP'].value:
      return QUESTION_TYPES.RANK.name;
    case QUESTION_TYPES.SEQUENTIAL_MONADIC.value:
      return QUESTION_TYPES.SEQUENTIAL_MONADIC.name;
    case QUESTION_TYPES.TABLE.value:
    case QUESTION_TYPES['TABLE-RADIO'].value:
      return QUESTION_TYPES.TABLE.name;
    case QUESTION_TYPES.TURF.value:
      return QUESTION_TYPES.TURF.name;
    case QUESTION_TYPES.VAN_WESTENDORP.value:
      return QUESTION_TYPES.VAN_WESTENDORP.name;
    case QUESTION_TYPES.VIDEO.value:
      return QUESTION_TYPES.VIDEO.name;
    default:
      return '';
  }
};

/**
 * Returns the subscription period string based on the subscription type.
 *
 * @param {Object} params
 * @param {string} params.type
 * @returns {string}
 */
export const getSubscriptionType = ({ type }) => {
  switch (type) {
    case 'MONTHLY':
      return 'Monthly';
    case 'QUARTERLY':
      return 'Quarterly';
    case 'YEARLY':
      return 'Yearly';
    default:
      return '';
  }
};

/**
 * Merges and filters an array of addon objects, returning only viewable addons with updated properties.
 * @param {Array<Object>} addonsArray - The array of addon objects to merge and filter.
 * @returns {Array<Object>}
 */
export const mergeAndFilterAddons = (addonsArray = []) => {
  if (!Array.isArray(addonsArray)) return [];

  return addonsArray
    .filter((addon) => addon?.name && VIEWABLE_ADDONS[addon.name])
    .map((addon) => {
      const view = VIEWABLE_ADDONS[addon.name];
      return {
        ...addon,
        displayName: view.displayName,
        selected: addon?.selected ?? false,
        value: view.value,
      };
    });
};
